"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const ServiceDefinition_1 = require("./ServiceDefinition");
const ParentServiceCompilerPass_1 = require("./ParentServiceCompilerPass");
const WidgetError_1 = require("../Error/WidgetError");
/**
 * Simple dependency injection container.
 */
class Container {
    /**
     * Constructs container.
     *
     * @param {*} services
     */
    constructor(services = {}) {
        /**
         * Services definition map.
         *
         * @type {Map<string, ServiceDefinition>}
         */
        this.serviceDefinitions = new Map();
        /**
         * Container compiler pass list.
         *
         * @type {{pass: ICompilerPass, weight: number}[]}
         */
        this.compilerPasses = [];
        /**
         * Tagged services.
         *
         * @type {*}
         */
        this.taggedServices = {};
        /**
         * Container parameters.
         *
         * @type {*}
         */
        this.parameters = {};
        /**
         * Compiled services keyed by ID.
         *
         * @type {*}
         */
        this.compiledServices = {};
        /**
         * Whether the container has been compiled.
         *
         * @type {boolean}
         */
        this.compiled = false;
        for (let [id, definition] of Object.entries(services)) {
            this.addDefinition(id, definition);
        }
    }
    /**
     * Registers/compiles the Services into a container.
     */
    compile() {
        if (this.compiled) {
            throw new Error(`Container cannot be compiled twice.`);
        }
        this.addCompilerPass(new ParentServiceCompilerPass_1.default(), 1000);
        this.compilerPasses.sort((a, b) => a.weight - b.weight)
            .forEach(({ pass }) => pass.process(this));
        this.compiled = true;
    }
    /**
     * Adds service definition.
     *
     * @param {string} id
     * @param {*|ServiceDefinition} value
     * @return {this}
     */
    addDefinition(id, value) {
        if (this.compiled) {
            throw new Error(`Cannot add definition. Container already compiled.`);
        }
        if (value instanceof ServiceDefinition_1.default) {
            this.serviceDefinitions.set(id, value);
        }
        else {
            this.serviceDefinitions.set(id, ServiceDefinition_1.default.fromObject(id, value));
        }
        return this;
    }
    /**
     * Gets service definition.
     *
     * @param {string} name
     * @return {ServiceDefinition | null}
     */
    getDefinition(name) {
        return this.serviceDefinitions.get(name);
    }
    /**
     * Gets all service definitions.
     *
     * @return {ServiceDefinition[]}
     */
    getDefinitions() {
        return Array.from(this.serviceDefinitions.values());
    }
    /**
     * Adds compiler pass.
     *
     * @param {ICompilerPass} compilerPass
     * @param {int} weight
     */
    addCompilerPass(compilerPass, weight = 0) {
        if (this.compiled) {
            throw new Error(`Cannot add compiler pass. Container already compiled.`);
        }
        this.compilerPasses.push({ pass: compilerPass, weight: weight });
        return this;
    }
    /**
     * Sets container parameter.
     *
     * @param {string} key
     * @param {*} value
     * @return {Container}
     */
    setParameter(key, value) {
        if (this.compiled) {
            throw new Error(`Cannot set parameter. Container already compiled.`);
        }
        this.parameters[key] = value;
        return this;
    }
    /**
     * Sets container parameters.
     *
     * @param {Object} params
     * @return {Container}
     */
    setParameters(params) {
        if (this.compiled) {
            throw new Error(`Cannot set parameters. Container already compiled.`);
        }
        Object.entries(params)
            .forEach(([key, value]) => { this.parameters[key] = value; });
        return this;
    }
    /**
     * Gets container parameters value.
     *
     * @param {string} key
     * @return {*}
     */
    getParameter(key) {
        return this.parameters[key];
    }
    /**
     * Finds tagged services.
     *
     * @param {string} tag
     * @return {*}
     */
    findTaggedServices(tag) {
        if (this.taggedServices[tag]) {
            return this.taggedServices[tag];
        }
        let results = {};
        Object.values(this.serviceDefinitions).forEach((definition) => {
            if (definition.hasTag(tag)) {
                results[definition.getId()] = this.compiledServices[definition.getId()];
            }
        });
        this.taggedServices[tag] = results;
        return results;
    }
    /**
     * Finds tagged service definitions.
     *
     * @param {string} tag
     * @return {ServiceDefinition[]}
     */
    findTaggedDefinitions(tag) {
        let results = [];
        this.getDefinitions().forEach((definition) => {
            if (definition.hasTag(tag)) {
                results.push(definition);
            }
        });
        return results;
    }
    /**
     * Processes a single service in the service config entry.
     *
     * @param {ServiceDefinition} definition
     *   The service definition.
     *
     * @returns {object}
     */
    processService(definition) {
        const argumentList = [];
        const id = definition.getId();
        if (definition.getArguments() !== undefined) {
            for (let argument of definition.getArguments()) {
                argumentList.push(this.processArgument(argument));
            }
        }
        let instance = this.compiledServices[id] ?
            this.compiledServices[id] :
            new (definition.getClass())(...argumentList);
        if (definition.isShared()) {
            this.compiledServices[id] = instance;
        }
        return instance;
    }
    /**
     * Processes an argument of a service definition.
     *
     * @param {*} argument
     *   The name of a service, name of parameter or actual param.
     *
     * @returns {*}
     */
    processArgument(argument) {
        if (typeof argument !== "string") {
            return argument;
        }
        if (argument.charAt(0) === "@") {
            argument = argument.substr(1);
            if (argument === "container") {
                return this;
            }
            let definition = this.findDefinition(argument);
            if (!definition) {
                throw new Error(`Service '${argument}' does not exist.`);
            }
            return this.processService(definition);
        }
        if (argument.charAt(0) === "%") {
            return this.getParameter(argument.substr(1));
        }
        return argument;
    }
    /**
     * Finds a service definition in the service config object.
     *
     * @param key
     *   Then name of the service.
     *
     * @returns {ServiceDefinition|null}
     */
    findDefinition(key) {
        if (this.serviceDefinitions.has(key)) {
            return this.serviceDefinitions.get(key);
        }
        return null;
    }
    /**
     * Creates service from definition.
     *
     * @param {string} name
     * @return {*}
     */
    createService(name) {
        const definition = this.serviceDefinitions.get(name);
        if (!definition) {
            throw new WidgetError_1.default(`The service with id ${name} does not exist.`);
        }
        return this.processService(definition);
    }
    /**
     * Returns an instance of the service.
     *
     * @param name
     *   The service name.
     * @returns {*}
     */
    get(name) {
        if (this.compiledServices[name]) {
            return this.compiledServices[name];
        }
        else {
            return this.createService(name);
        }
    }
    /**
     * Clones the compiled container.
     *
     * @return {Container}
     */
    clone() {
        if (this.compiled) {
            throw new WidgetError_1.default(`Compiled container's cannot be cloned.`);
        }
        const container = new Container();
        this.serviceDefinitions.forEach((service) => container.addDefinition(service.getId(), service.clone()));
        container.parameters = Object.assign({}, this.parameters);
        container.compilerPasses = Array.from(this.compilerPasses);
        return container;
    }
}
exports.Container = Container;
